import type React from "react"
import { useState, useRef, useEffect, useCallback } from "react"
import CheckBoxWithLabel from "../checkbox/CheckBoxWithLabel"
import CustomRadioButton from "../buttons/CustomRadioButton"
import CustomDatePicker from "../DatePicker/CustomDatePicker"
import Button from "../buttons/Button"
import ErrorMessage from "../ErrorMessages/ErrorMessage"
import useTranslation from "../CustomHooks/useTranslation"
import { breadcrumbsContent } from "../breadcrumbs/BreadcrumbsContent"
import dayjs from "dayjs"
import ButtonWithImg from "../buttons/ButtonWithImg"
import SearchWithDropdown from "../../components/Dropdowns/SearchWithDropdown"
import { createPortal } from "react-dom"
import { getUniqueRegions, getUniqueProvinces, getUniqueDistricts, getAllLocationData } from "../utils/preProcessLocation"

// Filter option type
export type DynamicFilterConfig = {
  [section: string]: {
    label: string
    type: "checkbox" | "radio" | "date" | "dropdown" | "dateRangeDropdown"
    options?: string[] | Record<string, any>[]
    default?: string | string[]
    required?: boolean
    searchKey: string | { start: string; end: string } | { date: string; start: string; end: string }
    valueMapping?: { [displayValue: string]: string }
    reverseMapping?: { [backendValue: string]: string }
    subFilters?: {
      [key: string]: { label: string; placeholder: string; searchKey: string }
    }
    ticketList?: boolean
  }
}

interface DynamicFilterPanelProps {
  config: DynamicFilterConfig
  onApply: (filters: { [key: string]: any }) => void
  onClear?: () => void
  initialValues?: { [key: string]: any }
  loading?: boolean
  disabled?: boolean
  className?: string
  customHeight?: string
  searchParams?: URLSearchParams
  balanceSheet?: boolean
  ticketList?: boolean
}

const DynamicFilterPanel: React.FC<DynamicFilterPanelProps> = ({
  config,
  onApply,
  onClear,
  initialValues = {},
  loading = false,
  disabled = false,
  className = "",
  customHeight,
  searchParams,

  ticketList
}) => {
  const { translate, notEnglish } = useTranslation(breadcrumbsContent)

  const [activeSection, setActiveSection] = useState<string>(() => {
    if (searchParams) {
      for (const [section, conf] of Object.entries(config)) {
        if (conf.type === "date" && typeof conf.searchKey === "object") {
          if (searchParams.get(conf.searchKey.start) || searchParams.get(conf.searchKey.end)) {
            return section;
          }
        } 
        else if (conf.type === "dateRangeDropdown" && typeof conf.searchKey === "object" && "date" in conf.searchKey) {
          if (searchParams.get(conf.searchKey.date)) {
            return section;
          }
        }
        else if (typeof conf.searchKey === "string" && searchParams.get(conf.searchKey)) {
          return section;
        }
      }
    }
    return Object.keys(config)[0] || "";
  })
  const [values, setValues] = useState<{ [key: string]: any }>(initialValues)
  const [errorMessage, setErrorMessage] = useState<string | { start?: string; end?: string }>("")
  const panelRef = useRef<HTMLDivElement | null>(null)
  const filterPanelRef = useRef<HTMLDivElement | null>(null)
  const filterContentRef = useRef<HTMLDivElement | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0, width: 0 })
  const isDatePickerOpenRef = useRef(false)

  // ✅ State for location dropdowns
  const [selectedCountry, setSelectedCountry] = useState<{ name: string; id: string } | null>(null)
  const [selectedRegion, setSelectedRegion] = useState<{ name: string; id: string } | null>(null)
  const [selectedProvince, setSelectedProvince] = useState<{ name: string; id: string } | null>(null)
  const [selectedDistrict, setSelectedDistrict] = useState<{ name: string; id: string } | null>(null)
  const [openDropdown, setOpenDropdown] = useState<"country" | "region" | "province" | "district" | "dateRange" | null>(null)
  
  // Refs for location dropdowns to enable auto-scroll
  const countryDropdownRef = useRef<HTMLDivElement | null>(null)
  const provinceDropdownRef = useRef<HTMLDivElement | null>(null)
  const regionDropdownRef = useRef<HTMLDivElement | null>(null)
  const districtDropdownRef = useRef<HTMLDivElement | null>(null)

  // ✅ State for date range dropdown
  const [selectedDateRange, setSelectedDateRange] = useState<string>("")
  const [customDateRange, setCustomDateRange] = useState<{ start: dayjs.Dayjs | null; end: dayjs.Dayjs | null }>({ start: null, end: null })

  const [locationData, setLocationData] = useState({ countries: [] as { name: string; id: string }[], regions: [] as { name: string; id: string }[], provinces: [] as { name: string; id: string }[], districts: [] as { name: string; id: string }[] })

  // Date range dropdown options
  const dateRangeOptions = ["Today", "Yesterday", "This Month", "This Year", "Custom"]

  // Helper function to get date range based on selection
  const getDateRange = (range: string) => {
    const today = dayjs()
    switch (range) {
      case "Today":
        return { start: today.startOf('day'), end: today.endOf('day') }
      case "Yesterday": {
        const yesterday = today.subtract(1, 'day')
        return { start: yesterday.startOf('day'), end: yesterday.endOf('day') }
      }
      case "This Month":
        return { start: today.startOf('month'), end: today.endOf('month') }
      case "This Year":
        return { start: today.startOf('year'), end: today.endOf('year') }
      case "Custom":
        return { start: null, end: null }
      default:
        return { start: null, end: null }
    }
  }

  useEffect(() => {
    setLocationData(getAllLocationData())
  }, [])

  // Helper function to check if element is inside date picker dropdown
  const isInsideDatePicker = (element: HTMLElement | null): boolean => {
    if (!element) return false;
    
    // Check if element itself or any parent has ant-picker classes
    let current: HTMLElement | null = element;
    while (current && current !== document.body) {
      // Check for various ant-picker selectors
      if (
        current.classList?.contains('ant-picker-dropdown') ||
        current.classList?.contains('ant-picker-panel') ||
        current.classList?.contains('ant-picker-calendar') ||
        current.classList?.contains('ant-picker-body') ||
        current.classList?.contains('ant-picker-content') ||
        current.classList?.contains('ant-picker-cell') ||
        current.classList?.contains('ant-picker-header') ||
        current.classList?.contains('ant-picker-prev-btn') ||
        current.classList?.contains('ant-picker-next-btn') ||
        current.classList?.contains('ant-picker-prev-icon') ||
        current.classList?.contains('ant-picker-next-icon') ||
        current.parentElement?.classList?.contains('ant-picker-dropdown')
      ) {
        return true;
      }
      // Also check if element is inside ant-picker-dropdown by querySelector
      const datePickerDropdown = document.querySelector('.ant-picker-dropdown');
      if (datePickerDropdown && datePickerDropdown.contains(current)) {
        return true;
      }
      current = current.parentElement;
    }
    return false;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Check if click is inside the button
      if (panelRef.current && panelRef.current.contains(target)) {
        return;
      }

      // Check if click is inside date picker dropdown
      if (isInsideDatePicker(target)) {
        isDatePickerOpenRef.current = true;
        return;
      }

      // Check if click is inside the portal filter panel
      const portalFilterPanel = document.querySelector('.dynamic-filter-panel');
      if (portalFilterPanel && portalFilterPanel.contains(target)) {
        // Close any open dropdown when clicking inside filter panel but outside dropdown
        setOpenDropdown(null);
        return;
      }

      // Check if click is inside any portal dropdown
      const portalDropdown = document.querySelector('.portal-dropdown');
      if (portalDropdown && portalDropdown.contains(target)) {
        return;
      }

      // Don't close if date picker was just open
      if (isDatePickerOpenRef.current) {
        isDatePickerOpenRef.current = false;
        return;
      }

      // If click is outside all panels, close everything
      setIsOpen(false);
      setErrorMessage('');
      setOpenDropdown(null);

      // Reset values to match current search params when closing without applying
      if (searchParams) {
        const derivedValues: { [key: string]: any } = {};

        Object.entries(config).forEach(([section, conf]) => {
          if (conf.type === "date" && typeof conf.searchKey === "object") {
            const startDate = searchParams.get(conf.searchKey.start);
            const endDate = searchParams.get(conf.searchKey.end);

            if (startDate || endDate) {
              derivedValues[section] = {
                start: startDate ? dayjs(startDate) : null,
                end: endDate ? dayjs(endDate) : null,
              };
            }
          } else if (conf.type === "dateRangeDropdown" && typeof conf.searchKey === "object" && "date" in conf.searchKey) {
            const dateValue = searchParams.get(conf.searchKey.date);
            const startDate = searchParams.get(conf.searchKey.start);
            const endDate = searchParams.get(conf.searchKey.end);

            if (dateValue) {
              setSelectedDateRange(dateValue);
              if (dateValue === "Custom" && startDate && endDate) {
                setCustomDateRange({
                  start: dayjs(startDate),
                  end: dayjs(endDate)
                });
              } else if (dateValue !== "Custom") {
                const dateRange = getDateRange(dateValue);
                setCustomDateRange(dateRange);
              }
            }
          } else if (typeof conf.searchKey === "string") {
            const paramValue = searchParams.get(conf.searchKey);
            if (paramValue) {
              if (conf.type === "checkbox") {
                const paramValues = paramValue.split(",");
                if (conf.reverseMapping) {
                  derivedValues[section] = paramValues.map(val => conf.reverseMapping![val] || val);
                } else {
                  derivedValues[section] = paramValues;
                }
              } else if (conf.type === "radio") {
                // Handle reverse value mapping for radio buttons
                if (conf.valueMapping) {
                  const reverseMapping = Object.fromEntries(
                    Object.entries(conf.valueMapping).map(([k, v]) => [v, k])
                  )
                  derivedValues[section] = reverseMapping[paramValue] || paramValue;
                } else {
                  derivedValues[section] = paramValue;
                }
              } else {
                derivedValues[section] = paramValue;
              }
            } else if (conf.type === "radio") {
              // If no search param for radio button, check if "All" exists and set as default
              const hasAllOption = (conf.options || []).some(opt =>
                (opt as string).toLowerCase() === "all"
              );
              if (hasAllOption) {
                derivedValues[section] = "All";
              } else if (conf.default) {
                derivedValues[section] = conf.default;
              }
            }
          }

          // Handle location dropdown initialization from searchParams
          if (conf.type === "dropdown" && conf.subFilters) {
            if (conf.subFilters.country) {
              const countryParam = searchParams.get(conf.subFilters.country.searchKey);
              if (countryParam) {
                setSelectedCountry({ name: countryParam, id: countryParam });
              }
            }
            if (conf.subFilters.province) {
              const provinceParam = searchParams.get(conf.subFilters.province.searchKey);
              if (provinceParam) {
                setSelectedProvince({ name: provinceParam, id: provinceParam });
              }
            }
            if (conf.subFilters.region) {
              const regionParam = searchParams.get(conf.subFilters.region.searchKey);
              if (regionParam) {
                setSelectedRegion({ name: regionParam, id: regionParam });
              }
            }
            if (conf.subFilters.district) {
              const districtParam = searchParams.get(conf.subFilters.district.searchKey);
              if (districtParam) {
                setSelectedDistrict({ name: districtParam, id: districtParam });
              }
            }
          }
        });

        setValues(derivedValues);
      } else {
        setValues({});
      }
      
      // Always reset to first section when closing
      setActiveSection(Object.keys(config)[0] || "");
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [searchParams, config]);

  useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0) setValues(initialValues)
  }, [initialValues])

  useEffect(() => {
    setErrorMessage("")
  }, [activeSection])

  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [isMobilePanel, setIsMobilePanel] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth <= 1024); // Check for 1024px and below
      setIsMobilePanel(window.innerWidth <= 900);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Function to update panel position based on button position
  const updatePanelPosition = useCallback(() => {
    if (panelRef.current) {
      const rect = panelRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth <= 900;
      const margin = 16;
      const panelWidth = isMobile
        ? window.innerWidth - margin * 2
        : ticketList && isSmallScreen ? 540 : 600;

      // Calculate estimated panel height from customHeight prop
      const heightMatch = customHeight?.match(/h-\[(\d+)px\]/);
      const estimatedContentHeight = heightMatch ? parseInt(heightMatch[1]) : 170;
      const panelHeight = estimatedContentHeight + (isMobile ? 200 : 120);

      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Decide whether to open above or below
      let topPosition;
      if (spaceBelow >= panelHeight + 20) {
        // Enough space below — open downward
        topPosition = rect.bottom + 8;
      } else if (spaceAbove >= panelHeight + 20) {
        // Not enough below but enough above — open upward
        topPosition = rect.top - panelHeight - 8;
      } else {
        // Neither side has enough room — pick the larger side and clamp
        if (spaceBelow >= spaceAbove) {
          topPosition = rect.bottom + 8;
        } else {
          topPosition = rect.top - panelHeight - 8;
        }
      }

      // Clamp top so panel never goes above viewport top
      topPosition = Math.max(margin, topPosition);
      // Clamp so panel never goes below viewport bottom
      const maxTop = viewportHeight - panelHeight - margin;
      topPosition = Math.min(topPosition, Math.max(margin, maxTop));

      // At md+ (>=768px) the sidebar is static at w-[300px], so content starts at 300px
      const sidebarWidth = window.innerWidth >= 768 ? 300 : 0;
      const contentAreaLeft = Math.max(margin, sidebarWidth);

      // Cap panel width to available space
      const availableWidth = window.innerWidth - contentAreaLeft - margin;
      const finalPanelWidth = Math.min(panelWidth, availableWidth);

      let leftPosition;
      if (isMobile) {
        leftPosition = contentAreaLeft;
      } else if (notEnglish) {
        leftPosition = rect.left;
      } else {
        // On tablet (641–1024px), align to button's left edge so panel opens rightward
        // On larger screens, align to button's right edge so panel opens leftward
        const isTablet = window.innerWidth <= 1024;
        if (isTablet) {
          leftPosition = rect.left;
        } else {
          leftPosition = rect.right - finalPanelWidth;
        }
      }

      // Clamp: never left of content area, never off right edge
      const maxLeft = window.innerWidth - finalPanelWidth - margin;
      leftPosition = Math.min(Math.max(contentAreaLeft, leftPosition), Math.max(contentAreaLeft, maxLeft));

      setPanelPosition({
        top: topPosition,
        left: leftPosition,
        width: finalPanelWidth
      });
    }
  }, [ticketList, isSmallScreen, customHeight, notEnglish]);

  // Update panel position on scroll and resize when panel is open
  useEffect(() => {
    if (!isOpen) return;

    // Update position initially
    updatePanelPosition();

    // Find scrollable parent containers
    const findScrollableParents = (element: HTMLElement | null): HTMLElement[] => {
      const scrollableParents: HTMLElement[] = [];
      let current: HTMLElement | null = element;
      
      while (current && current !== document.body) {
        const style = window.getComputedStyle(current);
        const overflowY = style.overflowY || style.overflow;
        const overflowX = style.overflowX || style.overflow;
        
        if (
          (overflowY === 'auto' || overflowY === 'scroll' || overflowX === 'auto' || overflowX === 'scroll') &&
          (current.scrollHeight > current.clientHeight || current.scrollWidth > current.clientWidth)
        ) {
          scrollableParents.push(current);
        }
        
        current = current.parentElement;
      }
      
      return scrollableParents;
    };

    // Add scroll listeners to window and all scrollable parents
    const scrollableParents = panelRef.current ? findScrollableParents(panelRef.current) : [];
    
    // Helper to check if scroll event is from inside the filter panel or its dropdowns
    const isScrollFromPanelOrDropdown = (event: Event): boolean => {
      const target = event.target as HTMLElement;
      if (!target) return false;
      
      // Check if scroll is from filter panel
      if (filterPanelRef.current && filterPanelRef.current.contains(target)) {
        return true;
      }
      
      // Check if scroll is from any portal dropdown (location dropdowns)
      const portalDropdowns = document.querySelectorAll('.portal-dropdown');
      for (const dropdown of portalDropdowns) {
        if (dropdown.contains(target)) {
          return true;
        }
      }
      
      // Check if scroll is from SearchWithDropdown component
      const searchDropdown = target.closest('.overflow-y-scroll');
      if (searchDropdown) {
        // Check if it's inside a portal dropdown
        const parentPortal = searchDropdown.closest('.portal-dropdown');
        if (parentPortal) {
          return true;
        }
      }
      
      return false;
    };
    
    const handleScroll = (event: Event) => {
      if (isScrollFromPanelOrDropdown(event)) {
        return;
      }
      setIsOpen(false);
    };
    
    const handleResize = () => updatePanelPosition();

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    
    scrollableParents.forEach(parent => {
      parent.addEventListener('scroll', handleScroll);
    });

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
      
      scrollableParents.forEach(parent => {
        parent.removeEventListener('scroll', handleScroll);
      });
    };
  }, [isOpen, updatePanelPosition]);

  const handleCheckbox = (section: string, option: string) => {
    setValues(prev => {
      const arr = Array.isArray(prev[section]) ? [...prev[section]] : []
      return arr.includes(option)
        ? { ...prev, [section]: arr.filter((v) => v !== option) }
        : { ...prev, [section]: [...arr, option] }
    })
  }

  const handleRadio = (section: string, value: string) => {
    setValues((prev) => ({ ...prev, [section]: value }))
  }

  const handleDateChange = (section: string, date: dayjs.Dayjs | null, type: "start" | "end") => {
    setValues((prev) => {
      const currentSectionValues = prev[section] || {}
      const newValues = { ...currentSectionValues, [type]: date }
      
      // Real-time validation for date range
      const startDate = type === "start" ? date : currentSectionValues.start
      const endDate = type === "end" ? date : currentSectionValues.end
      
      if (startDate && endDate) {
        if (startDate.isAfter(endDate)) {
          setErrorMessage({
            start: translate("Start date cannot be greater than end date"),
            end: translate("Start date cannot be greater than end date")
          })
        } else {
          setErrorMessage("")
        }
      } else {
        setErrorMessage("")
      }
      
      return { ...prev, [section]: newValues }
    })
  }

  const handleLocationChange = (type: "country" | "region" | "province" | "district", value: { name: string; id: string } | null) => {
    if (type === "country") {
      // Store Country dropdown position before changing
      const countryRect = countryDropdownRef.current?.getBoundingClientRect()
      const contentRect = filterContentRef.current?.getBoundingClientRect()
      const countryOffsetFromTop = countryRect && contentRect ? countryRect.top - contentRect.top + (filterContentRef.current?.scrollTop || 0) : 0
      
      setSelectedCountry(value)
      setSelectedProvince(null)
      setSelectedRegion(null)
      setSelectedDistrict(null)
      setLocationData(prev => ({ ...prev, provinces: getUniqueProvinces(value?.name || value?.id), regions: [], districts: [] }))
      
      // Auto-scroll to position Province dropdown where Country was
      setTimeout(() => {
        if (provinceDropdownRef.current && filterContentRef.current && value) {
          const provinceRect = provinceDropdownRef.current.getBoundingClientRect()
          const newContentRect = filterContentRef.current.getBoundingClientRect()
          const provinceOffsetFromTop = provinceRect.top - newContentRect.top + filterContentRef.current.scrollTop
          const scrollDiff = provinceOffsetFromTop - countryOffsetFromTop
          filterContentRef.current.scrollTo({ top: filterContentRef.current.scrollTop + scrollDiff, behavior: 'smooth' })
        }
      }, 150)
    } else if (type === "province") {
      // Store Province dropdown position before changing
      const provinceRect = provinceDropdownRef.current?.getBoundingClientRect()
      const contentRect = filterContentRef.current?.getBoundingClientRect()
      const provinceOffsetFromTop = provinceRect && contentRect ? provinceRect.top - contentRect.top + (filterContentRef.current?.scrollTop || 0) : 0
      
      setSelectedProvince(value)
      setSelectedRegion(null)
      setSelectedDistrict(null)
      setLocationData(prev => ({ ...prev, regions: getUniqueRegions(selectedCountry?.name || selectedCountry?.id, value?.name || value?.id), districts: getUniqueDistricts(selectedCountry?.name || selectedCountry?.id, value?.name || value?.id) }))
      
      // Auto-scroll to position Region dropdown where Province was
      setTimeout(() => {
        if (regionDropdownRef.current && filterContentRef.current && value) {
          const regionRect = regionDropdownRef.current.getBoundingClientRect()
          const newContentRect = filterContentRef.current.getBoundingClientRect()
          const regionOffsetFromTop = regionRect.top - newContentRect.top + filterContentRef.current.scrollTop
          const scrollDiff = regionOffsetFromTop - provinceOffsetFromTop
          filterContentRef.current.scrollTo({ top: filterContentRef.current.scrollTop + scrollDiff, behavior: 'smooth' })
        }
      }, 150)
    } else if (type === "region") {
      // Store Region dropdown position before changing
      const regionRect = regionDropdownRef.current?.getBoundingClientRect()
      const contentRect = filterContentRef.current?.getBoundingClientRect()
      const regionOffsetFromTop = regionRect && contentRect ? regionRect.top - contentRect.top + (filterContentRef.current?.scrollTop || 0) : 0
      
      setSelectedRegion(value)
      setSelectedDistrict(null)
      
      // Auto-scroll to position District dropdown where Region was
      setTimeout(() => {
        if (districtDropdownRef.current && filterContentRef.current && value) {
          const districtRect = districtDropdownRef.current.getBoundingClientRect()
          const newContentRect = filterContentRef.current.getBoundingClientRect()
          const districtOffsetFromTop = districtRect.top - newContentRect.top + filterContentRef.current.scrollTop
          const scrollDiff = districtOffsetFromTop - regionOffsetFromTop
          filterContentRef.current.scrollTo({ top: filterContentRef.current.scrollTop + scrollDiff, behavior: 'smooth' })
        }
      }, 150)
    } else if (type === "district") {
      setSelectedDistrict(value)
    }
  }

  const handleDateRangeChange = (range: string) => {
    setSelectedDateRange(range)
    if (range !== "Custom") {
      const dateRange = getDateRange(range)
      setCustomDateRange(dateRange)
      // Clear error message when switching to predefined range
      setErrorMessage("")
    } else {
      setCustomDateRange({ start: null, end: null })
      // Clear error message when switching to Custom
      setErrorMessage("")
    }
  }

  const handleCustomDateChange = (date: dayjs.Dayjs | null, type: "start" | "end") => {
    setCustomDateRange(prev => {
      const newRange = { ...prev, [type]: date }
      
      // Real-time validation for custom date range
      const startDate = type === "start" ? date : prev.start
      const endDate = type === "end" ? date : prev.end
      
      if (startDate && endDate) {
        if (startDate.isAfter(endDate)) {
          setErrorMessage({
            start: translate("Start date cannot be greater than end date"),
            end: translate("Start date cannot be greater than end date")
          })
        } else {
          // Check if the date range exceeds 365 days
          const daysDifference = endDate.diff(startDate, 'day');
          if (daysDifference > 365) {
            setErrorMessage({
              end: translate("Date range cannot exceed 365 days")
            })
          } else {
            setErrorMessage("")
          }
        }
      } else {
        setErrorMessage("")
      }
      
      return newRange
    })
  }

  // Date Range Dropdown wrapper
  const DateRangeDropdownWrapper = ({
    options, value, label, placeholder, disabled = false, error
  }: {
    options: string[]
    value: string
    label: string
    placeholder: string
    disabled?: boolean
    setErrorMessage?: (errorMessage: string | { start?: string; end?: string }) => void
    error?: string
  }) => {
    const triggerRef = useRef<HTMLDivElement>(null)
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
    const isDropdownOpen = openDropdown === "dateRange"

    useEffect(() => {
      if (isDropdownOpen && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect()
        const zoomLevel = window.devicePixelRatio || 1
        const isHighZoom = zoomLevel >= 1.1
        
        const top = isHighZoom 
          ? rect.top + window.scrollY - 260
          : rect.bottom + window.scrollY

        // Use at least the trigger width, but cap to available space
        const margin = 8
        const minWidth = Math.max(rect.width, 160)
        const maxWidth = window.innerWidth - margin * 2
        const dropdownWidth = Math.min(minWidth, maxWidth)

        const rawLeft = rect.left + window.scrollX
        const clampedLeft = Math.min(rawLeft, window.innerWidth - dropdownWidth - margin)
        const left = Math.max(margin, clampedLeft)

        setDropdownPosition({ top, left, width: dropdownWidth })
      }
    }, [isDropdownOpen])

    return (
      <div className="mb-4 w-[90%]">
        <label className="block text-sm text-primary_text font-[600] mb-2">{label}<span className="text-negative text-sm pl-1">*</span></label>
        <div className="relative">
          <div
            ref={triggerRef}
            className={`w-full p-2 ${error ? "border-negative" : "border-P_light_active"} h-[46px] border rounded-lg bg-white flex justify-between items-center ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
              }`}
            onClick={() => !disabled && setOpenDropdown(isDropdownOpen ? null : "dateRange")}
          >
            <span className={value ? "text-primary_text text-subTitle" : "text-subTitle text-secondary_text"}>{value || placeholder}</span>
            <svg
              className={`w-5 h-5 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          {isDropdownOpen && typeof window !== "undefined" && createPortal(
            <div
              className="portal-dropdown fixed z-[110] bg-white border rounded-md shadow-lg overflow-hidden"
              style={{ top: dropdownPosition.top, left: dropdownPosition.left, width: dropdownPosition.width, minWidth: '160px' }}
            >
              <SearchWithDropdown
                options={options}
                onSelect={(selectedValue) => {
                  handleDateRangeChange(selectedValue as string)
                  setOpenDropdown(null)
                  setErrorMessage("")
                }}
                handelDynamicValue={(item) => String(item)}
                id="dateRange-dropdown"
                dropdownTestId="dateRange-dropdown-item"
                className="w-full"
                notAbsolute={true}
                noSearch={true}
              />
            </div>,
            document.body
          )}
        </div>
      </div>
    )
  }

  // No flicker dropdown wrapper
  const LocationDropdownWrapper = ({
    type, options, value, label, placeholder, disabled = false, dropdownRef
  }: {
    type: "country" | "region" | "province" | "district"
    options: { name: string; id: string }[]
    value: { name: string; id: string } | null
    label: string
    placeholder: string
    disabled?: boolean
    dropdownRef?: React.RefObject<HTMLDivElement>
  }) => {
    const triggerRef = useRef<HTMLDivElement>(null)
    const portalDropdownRef = useRef<HTMLDivElement | null>(null)
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, positionAbove: false })
    const isDropdownOpen = openDropdown === type

    // Derive display text directly (no state = no flicker!)
    const displayValue = value ? (typeof value === "string" ? value : value.name || "") : ""

    // Function to calculate and update dropdown position
    const updateDropdownPosition = useCallback(() => {
      if (isDropdownOpen && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect()
        const dropdownMaxHeight = 320 // Max height of dropdown
        const gap = 4 // Small gap between trigger and dropdown
        const spaceBelow = window.innerHeight - rect.bottom
        const spaceAbove = rect.top
        
        // Get actual dropdown height if it's rendered
        let actualDropdownHeight = dropdownMaxHeight
        if (portalDropdownRef.current) {
          actualDropdownHeight = Math.min(portalDropdownRef.current.offsetHeight, dropdownMaxHeight)
        }
        
        // Calculate if positioning below would make the dropdown go off-screen
        const dropdownWouldExceedViewport = (rect.bottom + actualDropdownHeight + gap) > window.innerHeight
        
        // Check if we should position above (not enough space below, but enough space above)
        const shouldPositionAbove = (spaceBelow < actualDropdownHeight || dropdownWouldExceedViewport) && spaceAbove > spaceBelow && spaceAbove >= actualDropdownHeight
        
        let top: number
        if (shouldPositionAbove) {
          // Position above the trigger with a small gap
          top = rect.top + window.scrollY - actualDropdownHeight - gap
        } else {
          // Position below the trigger (default) with a small gap
          top = rect.bottom + window.scrollY + gap
        }
        
        const rawLeft = rect.left + window.scrollX
        const margin = 8
        const clampedLeft = Math.min(rawLeft, window.innerWidth - rect.width - margin)
        const left = Math.max(clampedLeft, margin)
        setDropdownPosition({
          top,
          left,
          width: rect.width,
          positionAbove: shouldPositionAbove
        })
      }
    }, [isDropdownOpen])

    useEffect(() => {
      updateDropdownPosition()
    }, [isDropdownOpen, updateDropdownPosition])

    // Recalculate position when options change (search results)
    useEffect(() => {
      if (isDropdownOpen) {
        // Small delay to allow dropdown to render with new height
        const timeoutId = setTimeout(() => {
          updateDropdownPosition()
        }, 100)
        return () => clearTimeout(timeoutId)
      }
    }, [options, isDropdownOpen, updateDropdownPosition])

    // Use ResizeObserver to watch for dropdown height changes
    useEffect(() => {
      if (!isDropdownOpen || !portalDropdownRef.current) return

      const resizeObserver = new ResizeObserver(() => {
        updateDropdownPosition()
      })

      resizeObserver.observe(portalDropdownRef.current)

      return () => {
        resizeObserver.disconnect()
      }
    }, [isDropdownOpen, updateDropdownPosition])

    return (
      <div ref={dropdownRef} className="mb-3">
        <label className="block text-body1 text-primary_text mb-1">{label}</label>
        <div className="relative pr-1">
          <div
            ref={triggerRef}
            className={`w-full p-2 border-P_light_active h-10 border rounded-lg bg-white flex justify-between items-center ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
              }`}
            onClick={() => !disabled && setOpenDropdown(isDropdownOpen ? null : type)}
          >
            <span className={`truncate ${displayValue ? "text-primary_text text-subTitle" : "text-subTitle text-secondary_text"}`}>{displayValue || placeholder}</span>
            <svg
              className={`w-5 h-5 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          {isDropdownOpen && typeof window !== "undefined" && createPortal(
            <div
              ref={portalDropdownRef}
              className="portal-dropdown fixed z-[110] bg-white border rounded-md shadow-lg"
              style={{ top: dropdownPosition.top, left: dropdownPosition.left, width: dropdownPosition.width, maxHeight: '320px' }}
            >
              <SearchWithDropdown
                options={options}
                onSelect={(selectedValue) => {
                  handleLocationChange(type, selectedValue as { name: string; id: string } | null)
                  setOpenDropdown(null)
                }}
                handelDynamicValue={(item) => (typeof item === "string" ? item : item.name || "")}
                id={`${type}-dropdown`}
                dropdownTestId={`${type}-dropdown-item`}
                className="w-full"
                notAbsolute={true}
              />
            </div>,
            document.body
          )}
        </div>
      </div>
    )
  }

  const handleClearAll = () => {
    setValues({});
    setSelectedCountry(null);
    setSelectedRegion(null);
    setSelectedProvince(null);
    setSelectedDistrict(null);
    setSelectedDateRange("");
    setCustomDateRange({ start: null, end: null });
    setErrorMessage("");
    setActiveSection(Object.keys(config)[0]);

    // Pass null to onApply to indicate "clear all filters" - this ensures URL params are deleted
    onApply({});
    onClear?.();

    setIsOpen(false);
    setOpenDropdown(null);
  };
  const handleApply = () => {
    // If the active section is a date filter, apply specific validation logic
    const activeConf = config[activeSection];
    if (activeConf && activeConf.type === "date") {
      const dateValue = values[activeSection] || {};
      const startDate = dateValue.start;
      const endDate = dateValue.end;
      const errors: { start?: string; end?: string } = {};
      if (!startDate && !endDate) {
        errors.start = translate("This field is required");
        errors.end = translate("This field is required");
        setErrorMessage(errors);
        return;
      }
      if (startDate && !endDate) {
        errors.end = translate("Please select end date");
        setErrorMessage(errors);
        return;
      }
      if (!startDate && endDate) {
        errors.start = translate("Please select start date");
        setErrorMessage(errors);
        return;
      }
      if (startDate && endDate && startDate.isAfter(endDate)) {
        setErrorMessage({ 
          start: translate("Start date cannot be greater than end date"),
          end: translate("Start date cannot be greater than end date")
        });
        return;
      }
    }

    // If the active section is a dateRangeDropdown filter, apply specific validation logic
    if (activeConf && activeConf.type === "dateRangeDropdown") {
      if (!selectedDateRange) {
        setErrorMessage(translate("Please select a date range"));
        return;
      }
      if (selectedDateRange === "Custom") {
        const errors: { start?: string; end?: string } = {};
        if(!customDateRange.start && !customDateRange.end) {
          errors.start = translate("This field is required");
          errors.end = translate("This field is required");
          setErrorMessage(errors);
          return;
        } else if (!customDateRange.start && customDateRange.end){
          errors.start = translate("Please select start date");
          setErrorMessage(errors);
          return;
        } else if (!customDateRange.end && customDateRange.start){
          errors.end = translate("Please select end date");
          setErrorMessage(errors);
          return;
        } else if (customDateRange.start && customDateRange.end && customDateRange.start.isAfter(customDateRange.end)) {
          errors.start = translate("Start date cannot be greater than end date");
          errors.end = translate("Start date cannot be greater than end date");
          setErrorMessage(errors);
          return;
        } else if (customDateRange.start && customDateRange.end) {
          // Check if the date range exceeds 365 days
          const daysDifference = customDateRange.end.diff(customDateRange.start, 'day');
          if (daysDifference > 365) {
            errors.end = translate("Date range cannot exceed 365 days");
            setErrorMessage(errors);
            return;
          }
        }
        setErrorMessage("");
      }
    }

    // Example: check required fields
    for (const key in config) {
      const conf = config[key];
      if (conf.required) {
        if (conf.type === "date") {
          if (!values[key]?.start || !values[key]?.end) {
            setErrorMessage(`${conf.label}: ${translate("Both dates required")}`);
            return;
          }
          // Date validation: start date cannot be after end date
          if (values[key].start && values[key].end && values[key].start.isAfter(values[key].end)) {
            setErrorMessage({ 
              start: `${conf.label}: ${translate("Start date cannot be greater than end date")}`,
              end: `${conf.label}: ${translate("Start date cannot be greater than end date")}`
            });
            return;
          }
        } else if (!values[key] || (Array.isArray(values[key]) && values[key].length === 0)) {
          setErrorMessage(`${conf.label}: ${translate("This field is required")}`);
          return;
        }
      }
    }

    // Additional date validations for all date fields
    for (const key in config) {
      const conf = config[key];
      if (conf.type === "date" && values[key]) {
        const dateValue = values[key];
        if (dateValue.start && dateValue.end && dateValue.start.isAfter(dateValue.end)) {
          setErrorMessage({ 
            start: `${conf.label}: ${translate("Start date cannot be greater than end date")}`,
            end: `${conf.label}: ${translate("Start date cannot be greater than end date")}`
          });
          return;
        }
        // Check if only one date is selected when both are required
        if (conf.required && ((dateValue.start && !dateValue.end) || (!dateValue.start && dateValue.end))) {
          setErrorMessage(`${conf.label}: ${translate("Both dates required")}`);
          return;
        }
      }
    }

    // Map values to search keys
    const mappedValues: { [key: string]: any } = {};
    Object.entries(config).forEach(([section, conf]) => {
      // Process ALL configured filters, not just those with values
      if (conf.type === "date" && typeof conf.searchKey === "object") {
        // For date filters, always set both start and end keys
        mappedValues[conf.searchKey.start] = values[section]?.start || null;
        mappedValues[conf.searchKey.end] = values[section]?.end || null;
      } else if (conf.type === "dateRangeDropdown" && typeof conf.searchKey === "object" && "date" in conf.searchKey) {
        // For dateRangeDropdown filters, set date, start_date, and end_date keys
        mappedValues[conf.searchKey.date] = selectedDateRange || null;
        if (selectedDateRange === "Custom") {
          mappedValues[conf.searchKey.start] = customDateRange.start || null;
          mappedValues[conf.searchKey.end] = customDateRange.end || null;
        } else if (selectedDateRange) {
          const dateRange = getDateRange(selectedDateRange);
          mappedValues[conf.searchKey.start] = dateRange.start || null;
          mappedValues[conf.searchKey.end] = dateRange.end || null;
        } else {
          mappedValues[conf.searchKey.start] = null;
          mappedValues[conf.searchKey.end] = null;
        }
      } else if (typeof conf.searchKey === "string") {
        const currentValue = values[section];

        // Handle empty/undefined values - clear the filter
        if (!currentValue || (Array.isArray(currentValue) && currentValue.length === 0)) {
          mappedValues[conf.searchKey] = null;
          return;
        }

        // Handle "All" values for radio buttons - use value mapping if available
        if (conf.type === "radio" && typeof currentValue === 'string' && currentValue.toLowerCase() === "all") {
          // Use value mapping if available, otherwise set to empty string
          if (conf.valueMapping && conf.valueMapping[currentValue] !== undefined) {
            const mappedValue = conf.valueMapping[currentValue];
            // Convert string "true"/"false" to actual boolean values, or keep as is for empty string
            if (mappedValue === "true") {
              mappedValues[conf.searchKey] = true;
            } else if (mappedValue === "false") {
              mappedValues[conf.searchKey] = false;
            } else {
              mappedValues[conf.searchKey] = mappedValue;
            }
          } else {
            mappedValues[conf.searchKey] = "";
          }
          return;
        }

        // Apply value mapping if available
        if (conf.valueMapping && Array.isArray(currentValue)) {
          // For checkbox arrays, map each value
          mappedValues[conf.searchKey] = currentValue.map((val: string) =>
            conf.valueMapping![val] || val
          );
        } else if (conf.valueMapping && typeof currentValue === 'string') {
          // For radio buttons, map the single value
          const mappedValue = conf.valueMapping[currentValue];
          if (mappedValue !== undefined) {
            // Convert string "true"/"false" to actual boolean values
            if (mappedValue === "true") {
              mappedValues[conf.searchKey] = true;
            } else if (mappedValue === "false") {
              mappedValues[conf.searchKey] = false;
            } else {
              mappedValues[conf.searchKey] = mappedValue;
            }
          } else {
            mappedValues[conf.searchKey] = currentValue;
          }
        } else {
          mappedValues[conf.searchKey] = currentValue;
        }
      }

    });

    // Handle location dropdown values - process after main config loop
    Object.entries(config).forEach(([, conf]) => {
      if (conf.type === "dropdown" && conf.subFilters) {
        if (conf.subFilters.country) {
          const countryValue = selectedCountry ? (selectedCountry.name || selectedCountry) : null;
          mappedValues[conf.subFilters.country.searchKey] = countryValue;
        }
        if (conf.subFilters.region) {
          mappedValues[conf.subFilters.region.searchKey] = selectedRegion ? (selectedRegion.name || selectedRegion) : null;
        }
        if (conf.subFilters.province) {
          mappedValues[conf.subFilters.province.searchKey] = selectedProvince ? (selectedProvince.name || selectedProvince) : null;
        }
        if (conf.subFilters.district) {
          mappedValues[conf.subFilters.district.searchKey] = selectedDistrict ? (selectedDistrict.name || selectedDistrict) : null;
        }
      }
    });
    setErrorMessage('');
    if (Object.keys(mappedValues).length === 0) {
      setActiveSection(Object.keys(config)[0]);
    }
    const filterDefaultValues = (mappedValues: { [key: string]: any }, config: DynamicFilterConfig) => {
      const filtered = { ...mappedValues };

      Object.entries(config).forEach(([, conf]) => {
        if (conf.default) {
          if (typeof conf.searchKey === 'string' && filtered[conf.searchKey] === conf.default) {
            filtered[conf.searchKey] = null;
          } else if (typeof conf.searchKey === 'object') {
            if (filtered[conf.searchKey.start] === conf.default) filtered[conf.searchKey.start] = null;
            if (filtered[conf.searchKey.end] === conf.default) filtered[conf.searchKey.end] = null;
          }
        }
        // Handle empty strings for radio buttons (e.g., "All" option that maps to "")
        // This ensures empty strings are converted to null so the URL parameter gets deleted
        if (typeof conf.searchKey === 'string' && conf.type === "radio" && filtered[conf.searchKey] === "") {
          filtered[conf.searchKey] = null;
        }
      });

      return filtered;
    };
    onApply(filterDefaultValues(mappedValues, config));
    setIsOpen(false);
  };

  return (
    <div className="flex justify-end">
      <div className={`relative z-[10] ${className}`} ref={panelRef}>
        <ButtonWithImg
          buttonName={translate("Filter")}
          ImgSrc={loading ? "loading-filter" : "filter"}
          className="px-4"
          buttonOnclick={() => {
            const newIsOpen = !isOpen;
            setIsOpen(newIsOpen);

            // Calculate position when opening
            if (newIsOpen) {
              updatePanelPosition();
            }

            // Reset values to match current search params when opening
            if (searchParams) {
              const derivedValues: { [key: string]: any } = {};

              Object.entries(config).forEach(([section, conf]) => {
                if (conf.type === "date" && typeof conf.searchKey === "object") {
                  const startDate = searchParams.get(conf.searchKey.start);
                  const endDate = searchParams.get(conf.searchKey.end);

                  if (startDate || endDate) {
                    derivedValues[section] = {
                      start: startDate ? dayjs(startDate) : null,
                      end: endDate ? dayjs(endDate) : null,
                    };
                  }
                } else if (conf.type === "dateRangeDropdown" && typeof conf.searchKey === "object" && "date" in conf.searchKey) {
                  const dateValue = searchParams.get(conf.searchKey.date);
                  const startDate = searchParams.get(conf.searchKey.start);
                  const endDate = searchParams.get(conf.searchKey.end);

                  if (dateValue) {
                    setSelectedDateRange(dateValue);
                    if (dateValue === "Custom" && startDate && endDate) {
                      setCustomDateRange({
                        start: dayjs(startDate),
                        end: dayjs(endDate)
                      });
                    } else if (dateValue !== "Custom") {
                      const dateRange = getDateRange(dateValue);
                      setCustomDateRange(dateRange);
                    }
                  }
                } else if (typeof conf.searchKey === "string") {
                  const paramValue = searchParams.get(conf.searchKey);
                  if (paramValue) {
                    if (conf.type === "checkbox") {
                      const paramValues = paramValue.split(",");
                      if (conf.reverseMapping) {
                        derivedValues[section] = paramValues.map(val => conf.reverseMapping![val] || val);
                      } else {
                        derivedValues[section] = paramValues;
                      }
                    } else if (conf.type === "radio") {
                      // Handle reverse value mapping for radio buttons
                      if (conf.valueMapping) {
                        const reverseMapping = Object.fromEntries(
                          Object.entries(conf.valueMapping).map(([k, v]) => [v, k])
                        )
                        derivedValues[section] = reverseMapping[paramValue] || paramValue;
                      } else {
                        derivedValues[section] = paramValue;
                      }
                    } else {
                      derivedValues[section] = paramValue;
                    }
                  } else if (conf.type === "radio") {
                    // If no search param for radio button, check if "All" exists and set as default
                    const hasAllOption = (conf.options || []).some(opt =>
                      (opt as string).toLowerCase() === "all"
                    );
                    if (hasAllOption) {
                      derivedValues[section] = "All";
                    } else if (conf.default) {
                      derivedValues[section] = conf.default;
                    }
                  }
                }

                // Handle location dropdown initialization from searchParams
                if (conf.type === "dropdown" && conf.subFilters) {
                  if (conf.subFilters.country) {
                    const countryParam = searchParams.get(conf.subFilters.country.searchKey);
                    if (countryParam) {
                      setSelectedCountry({ name: countryParam, id: countryParam });
                    }
                  }
                  if (conf.subFilters.province) {
                    const provinceParam = searchParams.get(conf.subFilters.province.searchKey);
                    if (provinceParam) {
                      setSelectedProvince({ name: provinceParam, id: provinceParam });
                    }
                  }
                  if (conf.subFilters.region) {
                    const regionParam = searchParams.get(conf.subFilters.region.searchKey);
                    if (regionParam) {
                      setSelectedRegion({ name: regionParam, id: regionParam });
                    }
                  }
                }
              });

              setValues(derivedValues);

              // Set active section to first matching filter in config order
              let firstMatchingSection = Object.keys(config)[0] || "";
              for (const [section, conf] of Object.entries(config)) {
                if (conf.type === "date" && typeof conf.searchKey === "object") {
                  if (searchParams.get(conf.searchKey.start) || searchParams.get(conf.searchKey.end)) {
                    firstMatchingSection = section;
                    break;
                  }
                } else if (conf.type === "dateRangeDropdown" && typeof conf.searchKey === "object" && "date" in conf.searchKey) {
                  if (searchParams.get(conf.searchKey.date)) {
                    firstMatchingSection = section;
                    break;
                  }
                } else if (typeof conf.searchKey === "string" && searchParams.get(conf.searchKey)) {
                  firstMatchingSection = section;
                  break;
                }
              }
              setActiveSection(firstMatchingSection);
            } else {
              setValues({});
              setActiveSection(Object.keys(config)[0] || "");
            }
            setErrorMessage('');
          }}
          disabledButton={!!loading || !!disabled}
          testId="filter"
        />

        {isOpen && typeof window !== "undefined" && createPortal(
          <div
            ref={filterPanelRef}
            className={`dynamic-filter-panel fixed z-[100] bg-white border border-s_dark_hover shadow-lg rounded-lg ${isMobilePanel ? 'pt-2 pb-3 px-3' : 'pt-[10px] pb-[16px] px-4'}`}
            style={{
              top: panelPosition.top,
              left: panelPosition.left,
              width: panelPosition.width
            }}
          >

            <p className="text-heading4 text-primary_text mb-4">{translate("Filter By")}</p>
            <hr className="2xl:mb-4 mb-2 border-neutral-300" />
            <div className={`flex ${customHeight || (isMobilePanel ? "h-[130px]" : "h-[170px]")}`}>
              {/* Sidebar */}
              <div className={`${notEnglish ? "border-l ml-4" : "border-r"} ${isMobilePanel ? "min-w-[90px]" : "min-w-[141px]"} h-full overflow-y-auto scrollBar`}>
                {Object.entries(config).map(([key, conf]) => (
                  <p
                    key={key}
                    className={`pb-4 pr-3 cursor-pointer text-title1 ${activeSection === key ? "text-primary_text" : "text-secondary_text"
                      }`}
                    onClick={() => setActiveSection(key)}
                    data-testid= {key}
                  >
                    {conf.label}
                  </p>
                ))}
              </div>

              {/* Main filter content */}
              <div
                ref={filterContentRef}
                className={`pl-4 ${config[activeSection]?.type === "dateRangeDropdown" ? 'pr-3' : ''} w-full h-full overflow-y-auto scrollBar ${ticketList ? "pr-2" : ""}`}>
                {(() => {
                  const conf = config[activeSection]
                  if (!conf) return null
                  if (conf.type === "checkbox") {
                    // Check if default value is "Failed to fetch" and show red text instead of checkboxes
                    if (conf.default === "Failed to fetch" && (conf.options as string[])?.includes("Failed to fetch")) {
                      return (
                        <div className="text-red-500 text-sm font-medium">
                          Failed to fetch
                        </div>
                      )
                    }
                    
                    return (conf.options || []).map((opt) => (
                      <CheckBoxWithLabel
                        key={opt as string}
                        label={opt as string}
                        handelCheckBox={() => handleCheckbox(activeSection, opt as string)}
                        checked={Array.isArray(values[activeSection]) && values[activeSection].includes(opt)}
                        id={opt as string}
                      />
                    ))
                  }
                  if (conf.type === "radio") {
                    // Get the current selected value or default to "All" if no search param exists
                    const getSelectedRadioValue = () => {
                      if (typeof values[activeSection] === "string") {
                        return values[activeSection]
                      }

                      // Check if there's a search param for this field
                      if (searchParams && typeof conf.searchKey === "string") {
                        const paramValue = searchParams.get(conf.searchKey)
                        if (paramValue) {
                          // If there's a param value, use reverse mapping if available
                          if (conf.valueMapping) {
                            const reverseMapping = Object.fromEntries(
                              Object.entries(conf.valueMapping).map(([k, v]) => [v, k])
                            )
                            return reverseMapping[paramValue] || paramValue
                          }
                          return paramValue
                        }
                      }

                      // If no search param and "All" exists in options, default to "All"
                      const hasAllOption = (conf.options || []).some(opt =>
                        (opt as string).toLowerCase() === "all"
                      )
                      if (hasAllOption) {
                        return "All"
                      }

                      // Otherwise use configured default or empty string
                      return conf.default || ""
                    }

                    return (
                      <CustomRadioButton
                        options={(conf.options || []).map((opt) => ({ id: opt as string, label: opt as string }))}
                        selected={getSelectedRadioValue() as string}
                        onChange={(val) => handleRadio(activeSection, val)}
                        gapClassName="gap-[6px]"
                      />
                    )
                  }
                  if (conf.type === "date") {
                    const errorObj = typeof errorMessage === "object" ? errorMessage : null
                    const hasStartError = errorObj && !!errorObj.start
                    const hasEndError = errorObj && !!errorObj.end
                    const hasRangeError = hasStartError && hasEndError && errorObj.start === errorObj.end && errorObj.start?.includes("cannot be greater")
                    const hasBothRequiredError = hasStartError && hasEndError && errorObj.start === errorObj.end && errorObj.start?.includes("This field is required")
                    const showCombinedError = hasRangeError || hasBothRequiredError
                    
                    return (
                      <div>
                        <div className={`flex ${isMobilePanel ? 'flex-col' : 'justify-between'} gap-3`}>
                          <CustomDatePicker
                            testID="start-date"
                            label={translate("Start Date")}
                            handleStates={(date) => handleDateChange(activeSection, date, "start")}
                            value={values[activeSection]?.start || null}
                            error={hasStartError && !showCombinedError ? (errorObj.start || "") : ""}
                            fieldError={hasStartError || false}
                            type="start"
                            classType="filter"
                            disabled={false}
                            required={true}
                          />
                          <CustomDatePicker
                            testID="end-date"
                            label={translate("End Date")}
                            handleStates={(date) => handleDateChange(activeSection, date, "end")}
                            value={values[activeSection]?.end || null}
                            error={hasEndError && !showCombinedError ? (errorObj.end || "") : ""}
                            fieldError={hasEndError || false}
                            type="end"
                            classType="filter"
                            disabled={false}
                            required={true}
                          />
                        </div>
                        {showCombinedError && errorObj && (
                          <div className="w-full">
                            <ErrorMessage 
                              testId="date-range-error" 
                              errorMessage={errorObj.start || ""}
                              paddingTop="pt-1 pb-2"
                            />
                          </div>
                        )}
                      </div>
                    )
                  }
                  if (conf.type === "dropdown" && activeSection === "Location") {
                    return (
                      <div className="space-y-2">
                        {conf.subFilters?.country && (
                          <LocationDropdownWrapper
                            type="country"
                            options={locationData.countries}
                            value={selectedCountry}
                            label={conf.subFilters.country.label}
                            placeholder={conf.subFilters.country.placeholder}
                            dropdownRef={countryDropdownRef}
                          />
                        )}
                        {conf.subFilters?.province && (
                          <LocationDropdownWrapper
                            type="province"
                            options={locationData.provinces}
                            value={selectedProvince}
                            label={conf.subFilters.province.label}
                            placeholder={conf.subFilters.province.placeholder}
                            disabled={!selectedCountry}
                            dropdownRef={provinceDropdownRef}
                          />
                        )}
                        {conf.subFilters?.region && (
                          <LocationDropdownWrapper
                            type="region"
                            options={locationData.regions}
                            value={selectedRegion}
                            label={conf.subFilters.region.label}
                            placeholder={conf.subFilters.region.placeholder}
                            disabled={!selectedProvince}
                            dropdownRef={regionDropdownRef}
                          />
                        )}
                        {conf.subFilters?.district && (
                          <LocationDropdownWrapper
                            type="district"
                            options={locationData.districts}
                            value={selectedDistrict}
                            label={conf.subFilters.district.label}
                            placeholder={conf.subFilters.district.placeholder}
                            disabled={!selectedProvince}
                            dropdownRef={districtDropdownRef}
                          />
                        )}
                      </div>
                    )
                  }
                  if (conf.type === "dateRangeDropdown") {
                    const stringError = typeof errorMessage === "string" && errorMessage && selectedDateRange !== "Custom"
                    return (
                      <div className="space-y-4">
                        <DateRangeDropdownWrapper
                          options={dateRangeOptions}
                          value={selectedDateRange}
                          label={conf.label}
                          placeholder="Select Date Range"
                          setErrorMessage={setErrorMessage}
                          error={stringError ? errorMessage : ""}
                        />
                        {stringError && (
                          <ErrorMessage 
                            testId="error-message" 
                            errorMessage={errorMessage}
                            paddingTop="pt-1 -mt-5 pb-2"
                          />
                        )}
                        {selectedDateRange === "Custom" && (
                          <div>
                            {(() => {
                              const errorObj = typeof errorMessage === "object" ? errorMessage : null
                              const hasStartError = errorObj && !!errorObj.start
                              const hasEndError = errorObj && !!errorObj.end
                              const hasRangeError = hasStartError && hasEndError && errorObj.start === errorObj.end && errorObj.start?.includes("cannot be greater")
                              const hasBothRequiredError = hasStartError && hasEndError && errorObj.start === errorObj.end && errorObj.start?.includes("This field is required")
                              const showCombinedError = hasRangeError || hasBothRequiredError || (hasStartError && hasEndError && errorObj.start === errorObj.end)
                              
                              return (
                                <>
                                  <div className={`flex ${isMobilePanel ? 'flex-col' : 'justify-between'} gap-3`}>
                                    <CustomDatePicker
                                      testID="custom-start-date"
                                      label={translate("Start Date")}
                                      handleStates={(date) => handleCustomDateChange(date, "start")}
                                      value={customDateRange.start}
                                      error={hasStartError && !showCombinedError ? (errorObj.start || "") : ""}
                                      fieldError={hasStartError || false}
                                      type="start"
                                      classType="filter"
                                      disabled={false}
                                      required={true}
                                    />
                                    <CustomDatePicker
                                      testID="custom-end-date"
                                      label={translate("End Date")}
                                      handleStates={(date) => handleCustomDateChange(date, "end")}
                                      value={customDateRange.end}
                                      error={hasEndError && !showCombinedError ? (errorObj.end || "") : ""}
                                      fieldError={hasEndError || false}
                                      type="end"
                                      classType="filter"
                                      disabled={false}
                                      required={true}
                                    />
                                  </div>
                                  {showCombinedError && errorObj && (
                                    <div className="w-full">
                                      <ErrorMessage 
                                        testId="custom-date-range-error" 
                                        errorMessage={errorObj.start || ""}
                                        paddingTop="pt-1 pb-2"
                                      />
                                    </div>
                                  )}
                                </>
                              )
                            })()}
                          </div>
                        )}
                      </div>
                    )
                  }
                  return null
                })()}
              </div>
            </div>

            {/* Footer buttons */}
            <div className="gap-3 flex pt-2">
              <Button
                buttonName={translate("Clear All")}
                textSize={isMobilePanel ? "text-body2" : "text-large"}
                buttonHight={isMobilePanel ? "h-8" : "h-10"}
                testId="clear_all"
                textColor="text-p_primary"
                className={`${isMobilePanel ? 'px-3' : 'px-4'} bg-white border border-p_primary`}
                buttonOnclick={handleClearAll}
                buttonDisabled={!!loading || !!disabled}
              />
              <Button
                buttonName={translate("Apply")}
                textSize={isMobilePanel ? "text-body2" : "text-large"}
                buttonHight={isMobilePanel ? "h-8" : "h-10"}
                testId="apply_filter"
                className={`${isMobilePanel ? 'px-3' : 'px-4'} bg-p_primary text-white`}
                buttonOnclick={() => handleApply()}
                buttonDisabled={!!loading || !!disabled}
              />
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  )
}

export default DynamicFilterPanel